import { useEffect, useMemo, useState } from "react";
import { Card, Empty, Spin, Table, Tag, Typography } from "antd";
import { useAuth } from "../../context/AuthContext";
import { useOrgUsers } from "../../context/OrgUsersContext";
import { fetchData } from "../../api/apiService";

// "Clients per Staff" widget (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.6).
// Admin-only — renders nothing for regular users / read_only / partner_admin.
// Renders nothing in single-user orgs unless there's at least one Shared
// client to surface (per the FE handoff's tightened polish — empty rows
// are noise).
//
// Data sources:
//   1. OrgUsersContext.users → per-user `assigned_clients_count` (3-portal
//      aggregate already computed by BE Phase 5; no FE fan-out).
//   2. GET /clients/{portal}?assigned_user_id=unassigned&page_size=1 ×3
//      → totals come from the response's `total` field. Only the count
//      is consumed; we never load the actual rows.

const { Text } = Typography;

const PORTALS = [
  { segment: "income-tax", label: "IT" },
  { segment: "gst", label: "GST" },
  { segment: "tds", label: "TDS" },
];

const ClientsPerStaffWidget = () => {
  const { user: currentUser } = useAuth();
  const { users, loaded, ensureLoaded, isSingleUserOrg } = useOrgUsers();
  const [unassignedTotal, setUnassignedTotal] = useState(null);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);

  const isAdmin =
    currentUser?.role === "tenant_admin" || currentUser?.role === "super_admin";

  useEffect(() => {
    if (!isAdmin) return;
    ensureLoaded();
  }, [isAdmin, ensureLoaded]);

  // Fetch the Shared-pool count once, sequentially across the 3 portals.
  // We don't bother polling — `Refresh` on the Team page or any reassign
  // through ReassignClientsModal triggers an OrgUsersContext refresh, but
  // the unassigned totals here are an admin overview not a tight feedback
  // loop. Re-mount the dashboard to refresh.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const run = async () => {
      setLoadingUnassigned(true);
      try {
        let sum = 0;
        for (const portal of PORTALS) {
          // page_size=1 — we only need `total` from the paginated
          // response. The 1-row payload is the smallest the BE will
          // serialise; not 0 because some BE wrappers treat that as
          // "give me everything" (defensive).
          const data = await fetchData(`/api/v1/clients/${portal.segment}`, {
            page: 1,
            page_size: 1,
            assigned_user_id: "unassigned",
          });
          if (cancelled) return;
          sum += Number(data?.total) || 0;
        }
        if (!cancelled) setUnassignedTotal(sum);
      } catch {
        // Best-effort. If any portal call fails, render with what we
        // have (likely null → "—").
        if (!cancelled) setUnassignedTotal(null);
      } finally {
        if (!cancelled) setLoadingUnassigned(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const rows = useMemo(() => {
    if (!isAdmin) return [];
    const userRows = users.map((u) => ({
      key: u.id,
      label: u.full_name ? `${u.full_name} (${u.email})` : u.email,
      role: u.role,
      count: typeof u.assigned_clients_count === "number" ? u.assigned_clients_count : 0,
      isShared: false,
    }));
    const sharedRow = {
      key: "_shared",
      label: "Shared (no one assigned)",
      role: "-",
      count: typeof unassignedTotal === "number" ? unassignedTotal : null,
      isShared: true,
    };
    // Sort highest count first; Shared row pinned to the bottom.
    userRows.sort((a, b) => (b.count || 0) - (a.count || 0));
    return [...userRows, sharedRow];
  }, [users, unassignedTotal, isAdmin]);

  if (!isAdmin) return null;

  // §3.8 polish: single-user org → only render if there's at least one
  // Shared client to show. Single-user + 0 Shared = entire widget is
  // noise (just the lone admin's count and a 0).
  if (isSingleUserOrg) {
    if (loadingUnassigned || unassignedTotal === null) return null;
    if (unassignedTotal === 0) return null;
  }

  if (!loaded) {
    return (
      <Card title="Clients per Staff" size="small" className="mt-4">
        <Spin />
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card title="Clients per Staff" size="small" className="mt-4">
        <Empty description="No team members yet. Invite a user to get started." />
      </Card>
    );
  }

  const columns = [
    {
      title: "User",
      dataIndex: "label",
      key: "label",
      render: (label, row) =>
        row.isShared ? (
          <Text type="secondary" italic>
            {label}
          </Text>
        ) : (
          label
        ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role, row) => {
        if (row.isShared) return <Text type="secondary">-</Text>;
        if (role === "tenant_admin") return <Tag color="blue">Admin</Tag>;
        if (role === "user") return <Tag>User</Tag>;
        if (role === "read_only") return <Tag color="default">Read-only</Tag>;
        return <Tag>{role}</Tag>;
      },
      width: 130,
    },
    {
      title: "Assigned Clients",
      dataIndex: "count",
      key: "count",
      align: "right",
      width: 160,
      render: (n) => (typeof n === "number" ? n : "-"),
    },
  ];

  return (
    <Card title="Clients per Staff" size="small" className="mt-4">
      <Table
        rowKey="key"
        size="small"
        dataSource={rows}
        columns={columns}
        pagination={false}
        loading={loadingUnassigned && unassignedTotal === null}
      />
    </Card>
  );
};

export default ClientsPerStaffWidget;
